import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';

import { IProduct } from 'src/app/models/products/product';
import { FilenameIdentifier } from 'src/app/models/product-data-filename-conventions/product-data-filename-convention';

export type ProductIdentifiersPopoverMode = 'info' | 'block';

export interface ExtraRow {
  readonly label: string;
  readonly value: string | null;
}

interface IdentifierRow {
  readonly key: FilenameIdentifier;
  readonly label: string;
  readonly value: string | null;
}

const IDENTIFIER_ORDER: readonly FilenameIdentifier[] = ['Barcode', 'Reference', 'Description'];

const IDENTIFIER_LABEL: Record<FilenameIdentifier, string> = {
  Barcode: 'Barcode',
  Reference: 'Reference',
  Description: 'Description',
};

const IDENTIFIER_TOKEN: Record<FilenameIdentifier, string> = {
  Barcode: '{barcode}',
  Reference: '{reference}',
  Description: '{description}',
};

@Component({
  selector: 'app-product-identifiers-popover',
  templateUrl: './product-identifiers-popover.component.html',
  styleUrls: ['./product-identifiers-popover.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class ProductIdentifiersPopoverComponent {
  @Input() product!: IProduct;
  @Input() pdfcIdentifier: FilenameIdentifier | null = null;
  @Input() mode: ProductIdentifiersPopoverMode = 'info';
  @Input() extraRows: readonly ExtraRow[] = [];
  @Output() readonly editRequested = new EventEmitter<IProduct>();

  isOpen = false;

  constructor(private hostRef: ElementRef<HTMLElement>) {}

  get identifierRows(): readonly IdentifierRow[] {
    return IDENTIFIER_ORDER.map((key: FilenameIdentifier) => ({
      key,
      label: IDENTIFIER_LABEL[key],
      value: this.identifierValue(key),
    }));
  }

  get filenameToken(): string | null {
    return this.pdfcIdentifier != null ? IDENTIFIER_TOKEN[this.pdfcIdentifier] : null;
  }

  isHighlighted(key: FilenameIdentifier): boolean {
    return this.pdfcIdentifier === key;
  }

  togglePopover(): void {
    this.isOpen = !this.isOpen;
  }

  closePopover(): void {
    this.isOpen = false;
  }

  requestEdit(): void {
    this.editRequested.emit(this.product);
    this.closePopover();
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(_event: Event): void {
    if (!this.isOpen) {
      return;
    }
    this.closePopover();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.isOpen) {
      return;
    }
    const target: Node | null = event.target as Node | null;
    if (target != null && this.hostRef.nativeElement.contains(target)) {
      return;
    }
    this.closePopover();
  }

  private identifierValue(key: FilenameIdentifier): string | null {
    if (this.product == null) {
      return null;
    }
    const raw: string | null =
      key === 'Barcode' ? this.product.barcode
      : key === 'Reference' ? this.product.reference
      : this.product.description;
    return raw != null && raw.trim().length > 0 ? raw : null;
  }
}
